import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import { firstValueFrom, Observable } from 'rxjs';
import { IBuilding, IResponse, ITenantLink } from '../core.interface';

@Injectable({
  providedIn: 'root',
})
export class Core {
  private http = inject(HttpClient);

  public buildings = signal<IBuilding[]>([]);
  public selectedBuilding = signal<IBuilding | null>(null);

  public async ensureLoaded(): Promise<void> {
    const tenantId = sessionStorage.getItem('tenantId');
    if (!tenantId) return;

    // Si ya tenemos el edificio en la Signal, no hacemos nada
    if (this.selectedBuilding()?.tenantId === tenantId) return;

    // Si no tenemos la lista de edificios, la cargamos
    if (this.buildings().length === 0) {
      await this.fetchBuildings();
      // fetchBuildings ya tiene tu lógica de buscar el tenantId en la lista
    }
  }

  public async fetchBuildings(reset: boolean = false): Promise<IBuilding[]> {
    if (this.buildings().length > 0 && reset === false) {
      return this.buildings();
    }

    try {
      const response = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}/tenant/list`, { observe: 'response' }),
      );

      if (response.status !== 200) {
        throw new Error('Error fetching buildings');
      }

      const data = response.body;
      const items: any[] = data?.['data'] ?? [];

      const mappedBuildings: IBuilding[] = items;
      this.buildings.set(mappedBuildings);

      // 5 Lógica de selección automática.
      if (mappedBuildings.length === 1) {
        this.selectBuilding(mappedBuildings[0]);
      }
      const tenantSessionRestored = sessionStorage.getItem('tenantId');
      const isSameTenantFromSameUser = mappedBuildings.findIndex(
        (e) => e.tenantId == tenantSessionRestored,
      );
      if (!!tenantSessionRestored && isSameTenantFromSameUser !== -1) {
        const indexTenant = mappedBuildings.findIndex((e) => e.tenantId == tenantSessionRestored);
        this.selectBuilding(mappedBuildings[indexTenant]);
      }

      return mappedBuildings;
    } catch (error) {
      console.error('Fetch buildings error:', error);
      throw error;
    }
  }

  public async getTenantFromLocal(): Promise<IBuilding | null> {
    if (this.buildings().length === 0) {
      await this.fetchBuildings();
    }
    const tenantSessionRestored = sessionStorage.getItem('tenantId');
    if (!!tenantSessionRestored) {
      const indexTenant = this.buildings().findIndex((e) => e.tenantId == tenantSessionRestored);
      this.selectBuilding(this.buildings()[indexTenant]);
    }
    return this.selectedBuilding();
  }

  public selectBuilding(building: IBuilding) {
    this.selectedBuilding.set(building);
    sessionStorage.setItem('tenantId', building.tenantId);
  }

  public async addTenantBuilding(data: ITenantLink): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${environment.apiUrl}/tenant/link`, data, { observe: 'response' }),
      );

      return response.status === 200;
    } catch (error) {
      console.error('Fetch buildings error:', error);
      return false;
    }
  }

  public clear(): void {
    this.buildings.set([]);
    this.selectedBuilding.set(null);
  }
}
